import {Expression as RExpression} from '@frontapp/workflows/expressions';

import {Expression, PreExpression} from '../../../settings/editors/expressions/expressionModel';
import {ExpressionsContext} from '../../../settings/editors/expressions/expressionsContext';
import {ExpressionState, ExpressionStateActions} from '../../../settings/editors/expressions/expressionState';
import {areInferredTypesEqual, UnknownResult} from '../../../settings/editors/expressions/infer/internal';

/*
 * Types.
 */

export interface FlowBuilderExpressionsEditor {
  state: ExpressionState;
  actions: ExpressionStateActions;
  context: ExpressionsContext;
  openCreateExpression: () => void;
  isExpressionListPanelVisible: boolean;
  openExpressionList: () => void;
  closeExpressionList: () => void;
}

type FlowBuilderExpressionsEditorActions = Pick<ExpressionStateActions, 'onSaveDraftExpression'>;

/*
 * Helpers.
 */

/**
 * Expression types are defined locally but also imported from @frontapp/workflows. These types are very close, but have some differences.
 * @frontapp/workflows defines "server" types, which do not care about the inferrence data. This is almost exactly what we call a "pre-expression" (as in: before inferrence).
 * Because the decision tree types are defined in libs/shared, they cannot import the local types and we end up having to pass "package" types to methods that expects local types.
 * There's probably a better solution, but this is good enough for now.
 */
export function castAsPreExpressions(rExpressions: ReadonlyArray<RExpression>): ReadonlyArray<PreExpression> {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return rExpressions as ReadonlyArray<PreExpression>;
}

/**
 * Checks whether the expression violates an integrity rule that will always cause it to evaluate to undefined.
 */
export function isExpressionInvalid(expression: Expression): boolean {
  return (
    areInferredTypesEqual(expression.inferredType, UnknownResult) ||
    expression.atoms.some((atom) => !atom.isValid)
  );
}

/**
 * Replaces actions on an existing ChatBotExpressionsEditor with variations that handle pushing to the state slice stack
 * and setting the state to dirty.
 */
export function applyDirtyExpressionsEditorActions(
  expressionsEditor: FlowBuilderExpressionsEditor,
  setIsDirty: (arg: boolean) => void,
  historyPush: () => void,
): FlowBuilderExpressionsEditor {
  // Set the state to dirty and push a new state slice onto the stack when saving a draft expression.
  const flowBuilderAction: FlowBuilderExpressionsEditorActions = {
    onSaveDraftExpression: (name?: string) => {
      setIsDirty(true);
      historyPush();
      expressionsEditor.actions.onSaveDraftExpression(name);
    },
  };

  return {
    ...expressionsEditor,
    actions: {
      ...expressionsEditor.actions,
      ...flowBuilderAction,
    },
  };
}
